import { computed, inject, Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { findIana } from 'windows-iana';
import { SettingsStore } from '@settings/data-access';

/**
 * Service responsible for handling date/time conversions between UTC and user's local timezone.
 * Automatically converts Windows Time Zone IDs to IANA timezone identifiers.
 */
@Injectable({ providedIn: 'root' })
export class DateTimeService {
  private readonly settingsStore = inject(SettingsStore);
  private readonly ownerSettings = computed(() => this.settingsStore.ownerSettings());

  /**
   * Gets the user's timezone in IANA format (e.g., "America/Sao_Paulo").
   * Converts from Windows Time Zone ID format if necessary.
   * @returns IANA timezone string or 'UTC' as fallback
   */
  public getUserZone(): string {
    const windowsTimezone = this.ownerSettings()?.Timezone;
    
    if (!windowsTimezone) {
      return 'UTC';
    }

    try {
      const ianaZones = findIana(windowsTimezone);
      
      if (ianaZones && ianaZones.length > 0) {
        return ianaZones[0];
      }
      
      const testZone = DateTime.now().setZone(windowsTimezone);
      if (testZone?.isValid) {
        return windowsTimezone;
      }
      return 'UTC';
    } catch {
      return 'UTC';
    }
  }

  /**
   * Converts all properties ending with "Utc" from UTC to local timezone.
   * Handles nested objects and arrays recursively.
   * @param obj - Object to convert
   * @returns New object with converted dates
   */
  public toLocalObject<T>(obj: T): T {
    if (!obj || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.toLocalObject(item)) as unknown as T;
    }

    if (obj instanceof Date) {
      return obj;
    }

    if (obj instanceof FormData || obj instanceof Blob) {
      return obj;
    }

    const userZone = this.getUserZone();
    const result = {} as T;

    for (const [key, value] of Object.entries(obj)) {
      if (key.endsWith('Utc') && typeof value === 'string') {
        const dateTime = DateTime.fromISO(value, { zone: 'utc' });
        
        if (dateTime.isValid) {
          const localDateTime = dateTime.setZone(userZone);
          const iso = localDateTime.toISO();
          (result as any)[key] = iso ?? value;
        } else {
          (result as any)[key] = value;
        }
      } else if (typeof value === 'object' && value !== null) {
        (result as any)[key] = this.toLocalObject(value);
      } else {
        (result as any)[key] = value;
      }
    }

    return result;
  }

  /**
   * Converts all properties ending with "Utc" from local timezone to UTC.
   * Handles nested objects and arrays recursively.
   * 
   * @param obj - Object to convert
   * @returns New object with converted dates
   */
  public toUtcObject<T>(obj: T): T {
    if (!obj || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.toUtcObject(item)) as unknown as T;
    }

    if (obj instanceof Date) {
      return obj;
    }

    if (obj instanceof FormData || obj instanceof Blob) {
      return obj;
    }

    const userZone = this.getUserZone();
    const result = {} as T;

    for (const [key, value] of Object.entries(obj)) {
      if (key.endsWith('Utc') && typeof value === 'string') {
        try {
          // 1. Remove any existing timezone suffix (Z or +/-HH:mm) and truncate microseconds 
          // to ensure we treat the string as "Local Wall Time" in the user's timezone.
          const cleanValue = value
            .replace(/Z$|[+-]\d{2}:\d{2}$/, '') // Strip Z or offset
            .replace(/(\.\d{3})\d+/, '$1');     // Truncate to 3 decimal places for Luxon

          // 2. Parse the wall time as being in the user's timezone
          let dateTime = DateTime.fromISO(cleanValue, { zone: userZone });
          
          if (dateTime.isValid) {
            // 3. Convert that local time to UTC
            const utcDateTime = dateTime.toUTC();
            const isoBase = utcDateTime.toISO();
            
            if (isoBase) {
              // Ensure exactly 6 decimal places (microseconds) by padding with zeros
              // Example: 2026-01-02T19:24:51.076Z -> 2026-01-02T19:24:51.076000Z
              // Example: 2026-01-02T19:24:51Z -> 2026-01-02T19:24:51.000000Z
              let isoString = isoBase;
              const millisecondMatch = isoString.match(/\.(\d+)(Z?)$/);
              if (millisecondMatch) {
                // Has milliseconds/microseconds, pad to 6 digits with zeros
                const digits = millisecondMatch[1];
                const zSuffix = millisecondMatch[2] || 'Z';
                
                if (digits === '999') {
                  isoString = isoString.replace(/\.\d+Z?$/, '.999999' + zSuffix);
                } else {
                  isoString = isoString.replace(/\.\d+Z?$/, '.' + digits.padEnd(6, '0') + zSuffix);
                }
              } else {
                // No milliseconds, add 6 zeros before Z
                isoString = isoString.replace(/Z$/, '.000000Z');
              }
              (result as any)[key] = isoString;
            } else {
              (result as any)[key] = value;
            }
          } else {
            (result as any)[key] = value;
          }
        } catch {
          (result as any)[key] = value;
        }
      } else if (typeof value === 'object' && value !== null) {
        (result as any)[key] = this.toUtcObject(value);
      } else {
        (result as any)[key] = value;
      }
    }

    return result;
  }

  /**
   * Formats a UTC ISO date string for display in the user's local timezone.
   * @param isoUtc - ISO date string in UTC
   * @param format - Optional Luxon format string (default: ISO format)
   * @returns Formatted date string or empty string if invalid
   */
  public formatForView(isoUtc: string | null | undefined, format?: string): string {
    if (!isoUtc) {
      return '';
    }

    try {
      const userZone = this.getUserZone();
      const dateTime = DateTime.fromISO(isoUtc, { zone: 'utc' });
      
      if (!dateTime.isValid) {
        return '';
      }

      const localDateTime = dateTime.setZone(userZone);
      
      if (format) {
        return localDateTime.toFormat(format);
      }
      
      return localDateTime.toISO() ?? '';
    } catch {
      return '';
    }
  }
}
